import { useQuery } from '@tanstack/react-query';

import { MIN_SEARCH_LENGTH } from '@/components/features/UsersList/constants';
import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

const LIMIT = 15;

export interface UseUsersAutocompleteOptions {
  organizationId: number;
  filter: string;
  enabled?: boolean;
}

export type UserAutocompleteResult = components['schemas']['GetUsersAutocompleteEndpoint_GetUsersResponse'];

export function useUsersAutocomplete({ organizationId, filter, enabled = true }: UseUsersAutocompleteOptions) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ['users-autocomplete', organizationId, filter],
    queryFn: async ({ signal }) => {
      const client = createApiClient(getAccessToken, {
        cache: 'no-store',
        signal,
      });

      const { data, error } = await client.GET('/api/organizations/{organizationId}/users/autocomplete', {
        params: {
          path: { organizationId },
          query: { Filter: filter, Limit: LIMIT },
        },
      });

      if (error) {
        throw new Error(error.title || 'Failed to fetch users');
      }

      return data ?? [];
    },
    enabled: enabled && filter.length >= MIN_SEARCH_LENGTH,
    staleTime: 30000,
  });
}
